import { getSiteLocalization } from '@/lib/i18n/config/site-locales'
import { parseDate, type CalendarDate } from '@internationalized/date'
import moment from 'moment'

type DateFormat =
  | 'COMPACT'
  | 'LONG'
  | 'DATABASE'
  | 'NORMAL'
  | 'FORMAL'
  | 'TIMESTAMP_FILENAME'
  | 'COMPACT_TIME'

/** ISO 8601 date format - use for database storage, API communication, and parsing ISO dates */
export const DATABASE_DATE_FORMAT = 'YYYY-MM-DD' as const

const getDateFormats = () => {
  const siteLocalization = getSiteLocalization()

  return {
    COMPACT: siteLocalization.dateFormats.compact,
    LONG: 'dddd Do MMMM', // Monday 1st January - locale agnostic
    DATABASE: DATABASE_DATE_FORMAT, // 2024-01-01 - ISO 8601 standard
    NORMAL: 'DD MMMM YYYY', // 01 January 2024 - locale agnostic (text month)
    FORMAL: 'DD MMMM', // 01 January - day and month only
    TIMESTAMP_FILENAME: 'YYYY-MM-DD_HH-mm-ss', // 2024-06-30_12-00-00 - ISO 8601 based
    COMPACT_TIME: siteLocalization.dateFormats.compactTime,
  } as const
}

/**
 * Builds a Moment object adjusted to the specified timezone.
 *
 * @param date - The date to parse. Accepts moment.MomentInput to support various date types (string, Date, number, etc.)
 *               (moment handles these gracefully, returning "Invalid date" for null/undefined)
 * @param timeZone - The timezone to apply
 * @returns A Moment object
 */
const buildMomentWithTimeZone = (date: moment.MomentInput, timeZone?: string) => {
  if (!date || !timeZone) return moment(date)

  const dateValue = moment(date).toDate()
  if (Number.isNaN(dateValue.getTime())) return moment(date)

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    })
      .formatToParts(dateValue)
      .reduce(
        (acc, { type, value }) => {
          if (type !== 'literal') acc[type] = value

          return acc
        },
        {} as Partial<Record<Intl.DateTimeFormatPartTypes, string>>,
      )

    const zonedDate = `${parts.year ?? '0000'}-${parts.month ?? '01'}-${parts.day ?? '01'}T${parts.hour ?? '00'}:${parts.minute ?? '00'}:${parts.second ?? '00'}`

    return moment(zonedDate)
  } catch {
    return moment(date)
  }
}

/**
 * Formats a date according to the specified format and timezone.
 *
 * @param date - The date to format. Accepts moment.MomentInput to support various date types (string, Date, number, etc.)
 *               (moment handles these gracefully, returning "Invalid date" for null/undefined)
 * @param format - The format type to use (COMPACT, LONG, DATABASE, etc.)
 * @param timeZone - The timezone to use (defaults to site's configured timezone)
 * @returns The formatted date string
 */
export const formatDate = (
  date: moment.MomentInput,
  format: DateFormat,
  timeZone = getSiteLocalization().timeZone,
) => {
  const dateFormats = getDateFormats()

  return buildMomentWithTimeZone(date, timeZone).format(dateFormats[format])
}

// CalendarDate is the value the react-aria date pickers exchange, DATABASE_DATE_FORMAT its
// serialized form. Shared here rather than in one picker, both sides parse the same strings
export const parseDatabaseDate = (
  value: string | null | undefined,
  // Names the caller in the warning, the pickers share this parser but not their failure modes
  context = 'date input',
): CalendarDate | null => {
  if (!value?.length) {
    return null
  }

  try {
    return parseDate(value)
  } catch {
    console.warn(`[${context}] Invalid date value, expected ${DATABASE_DATE_FORMAT}:`, value)

    return null
  }
}

export const formatDatabaseDate = (date: CalendarDate | null): string => date?.toString() ?? ''
